import type { FunctionComponent, ReactNode } from 'react';

import { Button, Input, TextArea } from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
import { Pill, TransformationStages } from 'components/admin';
import { VideoIntents } from 'components/admin/VideoIntents';
import { VideoModalities } from 'components/admin/VideoModalities';

import {
  type AiAnalysisState,
  MDASH,
  type VideoT,
  useGlobalState,
} from 'utils';

import inputTheme from 'themes/Input/admin/with-inner-fixed-small-label-60px.scss';

import BodyAreas from './BodyAreas';
import { DifficultyLevels } from './DifficultyLevels';
import Equipment from './Equipment';

import type { VideoSpecsT } from './common';

import S from './style.scss';

export type { VideoSpecsT };

type PropsT = {
  aiAnalysisState: AiAnalysisState | null | undefined;
  onRunAiAnalysis: () => void;
  setSpecs: (specs: VideoSpecsT) => void;
  specs: VideoSpecsT;
  submitted: boolean;
  video: VideoT | null;
};

const SpecsTab: FunctionComponent<PropsT> = ({
  aiAnalysisState,
  onRunAiAnalysis,
  setSpecs,
  specs,
  submitted,
  video,

  // eslint-disable-next-line complexity
}) => {
  const [permissions] = useGlobalState('user.permissions');
  const isAdmin = !!permissions?.EDIT_ANY_VIDEO_DETAILS;

  let bottom: ReactNode;

  if (aiAnalysisState === 'DONE') {
    const bodyPositionNodes: ReactNode[] = [];
    for (let i = 0; i < (video?.bodyPositions.length ?? 0); ++i) {
      const position = video?.bodyPositions[i];
      bodyPositionNodes.push(
        <Pill key={`${i}/${position}`} kind="blue">
          {position}
        </Pill>,
      );
    }

    const toLi = (s: string) => <li key={s}>{s}</li>;
    bottom = (
      <>
        <div styleName="section">
          <h3>Transformation Stages</h3>
          <TransformationStages stages={video?.transformationStages} />
        </div>
        <div styleName="section">
          <h3>Body Positions</h3>
          <div styleName="pills">
            {bodyPositionNodes.length ? bodyPositionNodes : MDASH}
          </div>
        </div>
        <div styleName="row">
          <Input
            label="Environment Needs"
            onChange={(e) => {
              setSpecs({ ...specs, environmentNeeds: e.target.value });
            }}
            theme={inputTheme}
            value={specs.environmentNeeds ?? ''}
          />
        </div>
        <div styleName="row">
          <VideoIntents
            intents={specs.intent ? [specs.intent] : []}
            onChange={(intents) => {
              setSpecs({ ...specs, intent: intents[0] });
            }}
            singleSelect
          />
          <DifficultyLevels setSpecs={setSpecs} specs={specs} />
          <BodyAreas setSpecs={setSpecs} specs={specs} />
          <VideoModalities
            customModalities={specs.customModalities}
            modalities={specs.modalities}
            onChange={(modalities, customModalities) => {
              setSpecs({
                ...specs,
                customModalities,
                modalities,
              });
            }}
          />
          <Equipment setSpecs={setSpecs} specs={specs} />
        </div>
        <div styleName="newMeta">
          <h3>Additional Video Traits</h3>
          <h4>Emotional Mechanisms</h4>
          <ul>{video?.emotionalMechanisms?.map(toLi)}</ul>
          <h4>Neurological Mechanisms</h4>
          <ul>{video?.neurologicalMechanisms?.map(toLi)}</ul>
          <h4>Physical Mechanisms</h4>
          <ul>{video?.physicalMechanisms?.map(toLi)}</ul>
          <h4>Additional Modalities</h4>
          <ul>{video?.modalitiesV2?.map(toLi)}</ul>
          <h4>Intended Shift</h4>
          <div styleName="shiftBlock">
            <div>{video?.shiftFrom?.join('; ')}</div>
            <div>&rArr;</div>
            <div>{video?.shiftTo?.join('; ')}</div>
          </div>
        </div>
      </>
    );
  } else if (isAdmin) {
    bottom = (
      <div styleName="pendingMetaDataGeneration">
        Pending generation of meta data by an admin.
      </div>
    );
  } else if (submitted) {
    bottom = (
      <div styleName="submitted">
        <h3>Thanks, your video has been submitted.</h3>
        <p>
          Our team will review it and add the final tagging before it goes live.
          We&apos;ll let you know once it&apos;s published.
        </p>
      </div>
    );
  }

  return (
    <div className={S.container}>
      <Input
        label="Video Title"
        onChange={(e) => {
          setSpecs({ ...specs, title: e.target.value });
        }}
        placeholder="Untitled"
        theme={inputTheme}
        value={specs.title ?? ''}
      />
      <TextArea
        disabled={!isAdmin}
        label="AI Video Analysis"
        onChange={(e) => {
          setSpecs({ ...specs, aiAnalysis: e.target.value });
        }}
        value={specs.aiAnalysis ?? ''}
      />
      <TextArea
        label="User Summary"
        onChange={(e) => {
          setSpecs({ ...specs, userSummary: e.target.value });
        }}
        value={specs.userSummary ?? ''}
      />
      {isAdmin ? (
        <>
          {aiAnalysisState === 'META_DATA_EXTRACTION_IN_PROGRESS' ? (
            <div styleName="aiMetaDataExtractionInProgress">
              AI Generation of Meta Data In Progress
              <Throbber />
            </div>
          ) : null}
          <Button
            disabled={!specs.aiAnalysis}
            onClick={onRunAiAnalysis}
          >
            (Re-) Start AI Generation of Meta Data
          </Button>
        </>
      ) : null}
      {bottom}
    </div>
  );
};

export default SpecsTab;
